import { isValidElement, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronDown, faChevronUp, faEye, faEyeSlash, faMagnifyingGlass, faXmark,
} from '@fortawesome/free-solid-svg-icons';

import Field, { blurOnEnter, useFieldIds } from './Field.jsx';

import './Input.css';

const cx = (...parts) => parts.filter(Boolean).join(' ');

// A FontAwesome definition, or a node boxed so .field-icon--node can size it.
const resolveIcon = (icon) => {
    if (!icon) return null;
    return isValidElement(icon)
        ? <span className="field-icon field-icon--node">{icon}</span>
        : <FontAwesomeIcon icon={icon} className="field-icon" />;
};

const shared = {
    id: PropTypes.string,
    label: PropTypes.node,
    labelEnd: PropTypes.node,
    helpText: PropTypes.node,
    status: PropTypes.oneOf(['neutral', 'danger']),
    isRequired: PropTypes.bool,
    isDisabled: PropTypes.bool,
    tooltip: PropTypes.string,
    placeholder: PropTypes.string,
    /* For a control used inline, with no visible label to point at. */
    ariaLabel: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
};

/* ── Text ── */

export const Input = ({
    id,
    label,
    ariaLabel,
    labelEnd,
    helpText,
    status = 'neutral',
    isRequired = false,
    isDisabled = false,
    readOnly = false,
    tooltip,
    variant = 'text',
    placeholder = '',
    icon,
    iconPosition = 'left',
    value,
    name,
    maxLength,
    autoFocus = false,
    onChange = () => {},
    onKeyDown = () => {},
    onBlur = () => {},
}) => {
    const { fieldId, messageId } = useFieldIds(id, !!helpText);

    const handleKeyDown = (event) => {
        blurOnEnter(event);
        onKeyDown(event);
    };

    const glyph = resolveIcon(icon);

    return (
        <Field
            id={fieldId}
            label={label}
            labelEnd={labelEnd}
            helpText={helpText}
            status={status}
            isRequired={isRequired}
            isDisabled={isDisabled}
            tooltip={tooltip}
        >
            <div className={cx('field-box', readOnly && 'field-box--readonly')}>
                {iconPosition === 'left' && glyph}
                <input
                    id={fieldId}
                    className="field-input"
                    type={variant}
                    name={name}
                    placeholder={placeholder}
                    disabled={isDisabled}
                    readOnly={readOnly}
                    required={isRequired}
                    maxLength={maxLength}
                    value={value}
                    autoFocus={autoFocus}
                    aria-label={ariaLabel}
                    aria-invalid={status === 'danger' || undefined}
                    aria-describedby={messageId}
                    onChange={onChange}
                    onKeyDown={handleKeyDown}
                    onBlur={onBlur}
                />
                {iconPosition === 'right' && glyph}
            </div>
        </Field>
    );
};

Input.propTypes = {
    ...shared,
    readOnly: PropTypes.bool,
    variant: PropTypes.string,
    icon: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.node]),
    iconPosition: PropTypes.oneOf(['left', 'right']),
    maxLength: PropTypes.number,
    autoFocus: PropTypes.bool,
    onKeyDown: PropTypes.func,
};

/* ── Textarea ── */

export const Textarea = ({
    id,
    label,
    ariaLabel,
    labelEnd,
    helpText,
    status = 'neutral',
    isRequired = false,
    isDisabled = false,
    readOnly = false,
    tooltip,
    placeholder = '',
    rows = 4,
    value,
    name,
    maxLength,
    // Opt-in: grow the field to fit its content, from `rows` lines (min) up to `maxRows` — then cap and
    // scroll. Off by default, so every other textarea keeps its fixed height.
    autoGrow = false,
    maxRows = null,
    autoFocus = false,
    onChange = () => {},
    // No blur-on-Enter here, unlike Input: Enter is a new line. A caller that sends on Enter handles it itself.
    onKeyDown = () => {},
    onBlur = () => {},
}) => {
    const { fieldId, messageId } = useFieldIds(id, !!helpText);
    const textareaRef = useRef(null);

    // Re-fit on every value change so a controlled field resizes as the user types (and on paste / a
    // programmatic set / initial hydration). Height is set inline, so it wins over the base rule.
    useEffect(() => {
        if (!autoGrow) return;
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        let next = el.scrollHeight;
        if (maxRows) {
            const cs = window.getComputedStyle(el);
            const line = parseFloat(cs.lineHeight) || (parseFloat(cs.fontSize) * 1.5) || 20;
            const chrome = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom)
                + parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
            const cap = Math.round(line * maxRows + chrome);
            el.style.overflowY = next > cap ? 'auto' : 'hidden';
            next = Math.min(next, cap);
        }
        el.style.height = `${next}px`;
    }, [value, autoGrow, maxRows]);

    return (
        <Field
            id={fieldId}
            label={label}
            labelEnd={labelEnd}
            helpText={helpText}
            status={status}
            isRequired={isRequired}
            isDisabled={isDisabled}
            tooltip={tooltip}
        >
            <div className={cx('field-box', 'field-box--textarea', readOnly && 'field-box--readonly')}>
                <textarea
                    ref={textareaRef}
                    id={fieldId}
                    className="field-input field-textarea"
                    name={name}
                    placeholder={placeholder}
                    disabled={isDisabled}
                    readOnly={readOnly}
                    required={isRequired}
                    maxLength={maxLength}
                    rows={autoGrow ? 1 : rows}
                    value={value}
                    autoFocus={autoFocus}
                    aria-label={ariaLabel}
                    aria-invalid={status === 'danger' || undefined}
                    aria-describedby={messageId}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    onBlur={onBlur}
                />
            </div>
        </Field>
    );
};

Textarea.propTypes = {
    ...shared,
    readOnly: PropTypes.bool,
    rows: PropTypes.number,
    maxLength: PropTypes.number,
    autoGrow: PropTypes.bool,
    maxRows: PropTypes.number,
    autoFocus: PropTypes.bool,
    onKeyDown: PropTypes.func,
};

/* ── Password ── */

export const Password = ({
    id,
    label,
    labelEnd,
    helpText,
    status = 'neutral',
    isRequired = false,
    isDisabled = false,
    tooltip,
    icon,
    placeholder = 'Enter password',
    value,
    name,
    autoFocus = false,
    onChange = () => {},
    onKeyDown = () => {},
    onBlur = () => {},
}) => {
    const { fieldId, messageId } = useFieldIds(id, !!helpText);
    const [isRevealed, setIsRevealed] = useState(false);

    const handleKeyDown = (event) => {
        blurOnEnter(event);
        onKeyDown(event);
    };

    return (
        <Field
            id={fieldId}
            label={label}
            labelEnd={labelEnd}
            helpText={helpText}
            status={status}
            isRequired={isRequired}
            isDisabled={isDisabled}
            tooltip={tooltip}
        >
            <div className="field-box">
                {resolveIcon(icon)}
                <input
                    id={fieldId}
                    className="field-input"
                    type={isRevealed ? 'text' : 'password'}
                    name={name}
                    placeholder={placeholder}
                    disabled={isDisabled}
                    required={isRequired}
                    value={value}
                    autoFocus={autoFocus}
                    aria-invalid={status === 'danger' || undefined}
                    aria-describedby={messageId}
                    onChange={onChange}
                    onKeyDown={handleKeyDown}
                    onBlur={onBlur}
                />
                {/* Swallowing mousedown keeps focus in the password box when the eye is clicked.
                    Without it the button took focus, so typing after a reveal went to the button
                    instead of the field — and the first keystroke flipped the browser into keyboard
                    modality, which painted the :focus-visible ring and made the icon look highlighted
                    out of nowhere. Tabbing to it still focuses it and still shows the ring, which is
                    what a focus ring is for. */}
                <button
                    type="button"
                    className="field-action"
                    disabled={isDisabled}
                    aria-label={isRevealed ? 'Hide password' : 'Show password'}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => setIsRevealed(!isRevealed)}
                >
                    <FontAwesomeIcon icon={isRevealed ? faEyeSlash : faEye} />
                </button>
            </div>
        </Field>
    );
};

Password.propTypes = {
    ...shared,
    icon: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.node]),
    autoFocus: PropTypes.bool,
    onKeyDown: PropTypes.func,
};

/* ── Search ── */

export const Search = ({
    id,
    label,
    labelEnd,
    helpText,
    status = 'neutral',
    isDisabled = false,
    placeholder = 'Search...',
    value,
    name,
    autoFocus = false,
    onChange = () => {},
    onKeyDown = () => {},
    onBlur = () => {},
}) => {
    const { fieldId, messageId } = useFieldIds(id, !!helpText);

    const handleKeyDown = (event) => {
        blurOnEnter(event);
        onKeyDown(event);
    };

    return (
        <Field
            id={fieldId}
            label={label}
            labelEnd={labelEnd}
            helpText={helpText}
            status={status}
            isDisabled={isDisabled}
        >
            <div className="field-box">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="field-icon" />
                <input
                    id={fieldId}
                    className="field-input"
                    type="search"
                    name={name}
                    placeholder={placeholder}
                    disabled={isDisabled}
                    value={value}
                    autoFocus={autoFocus}
                    aria-label={label ? undefined : (placeholder || 'Search')}
                    aria-invalid={status === 'danger' || undefined}
                    aria-describedby={messageId}
                    onChange={onChange}
                    onKeyDown={handleKeyDown}
                    onBlur={onBlur}
                />
                {/* Same reason as the password eye, plus one of its own: this button unmounts the
                    moment the value clears, so if it had taken focus the page would be left with
                    focus on nothing and the user could not simply keep typing. */}
                {value && !isDisabled && (
                    <button
                        type="button"
                        className="field-action"
                        aria-label="Clear search"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => onChange({ target: { name, value: '' } })}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                )}
            </div>
        </Field>
    );
};

Search.propTypes = {
    ...shared,
    autoFocus: PropTypes.bool,
    onKeyDown: PropTypes.func,
};

/* ── Number ── */

// Decimal places, tolerating scientific notation (1e-7), so stepping can round to a sane precision
// instead of exposing binary floating-point noise like 0.24 - 0.1 = 0.13999999999999999.
const countDecimals = (n) => {
    if (!Number.isFinite(n)) return 0;
    const s = String(n);
    if (s.includes('e') || s.includes('E')) {
        const [mantissa, exp] = s.toLowerCase().split('e');
        const dec = (mantissa.split('.')[1] || '').length;
        return Math.max(0, dec - Number(exp));
    }
    return (s.split('.')[1] || '').length;
};

export const NumberInput = ({
    id,
    label,
    labelEnd,
    helpText,
    status = 'neutral',
    isRequired = false,
    isDisabled = false,
    tooltip,
    placeholder = '',
    ariaLabel,
    min,
    max,
    step,
    value,
    name,
    autoFocus = false,
    onChange = () => {},
    onKeyDown = () => {},
    onBlur = () => {},
}) => {
    const { fieldId, messageId } = useFieldIds(id, !!helpText);
    const inputRef = useRef(null);

    const minNum = parseFloat(min);
    const maxNum = parseFloat(max);
    const hasMin = !Number.isNaN(minNum);
    const hasMax = !Number.isNaN(maxNum);
    const blockNegativeChars = hasMin && minNum >= 0;

    // Latest value in a ref so the press-and-hold interval reads the current number each tick
    // rather than the one captured when the interval was created.
    const valueRef = useRef(value);
    valueRef.current = value;
    const holdTimeoutRef = useRef(null);
    const holdIntervalRef = useRef(null);

    /* The wheel must never change the value. Guarded on focus so the page still scrolls when the
       pointer merely passes over an unfocused field, and non-passive so preventDefault applies. */
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return undefined;
        const stop = (event) => {
            if (document.activeElement === el) event.preventDefault();
        };
        el.addEventListener('wheel', stop, { passive: false });
        return () => el.removeEventListener('wheel', stop);
    }, []);

    const adjust = (dir) => {
        if (isDisabled) return;
        const stepVal = parseFloat(step) || 1;
        const current = parseFloat(valueRef.current) || 0;
        let newVal = dir === 'up' ? current + stepVal : current - stepVal;
        const decimals = Math.min(Math.max(countDecimals(stepVal), countDecimals(current)), 12);
        newVal = Number(newVal.toFixed(decimals));
        if (hasMin) newVal = Math.max(minNum, newVal);
        if (hasMax) newVal = Math.min(maxNum, newVal);
        onChange({ target: { name, value: String(newVal) } });
    };

    const stopHold = () => {
        clearTimeout(holdTimeoutRef.current);
        clearInterval(holdIntervalRef.current);
        holdTimeoutRef.current = null;
        holdIntervalRef.current = null;
    };

    const startHold = (dir) => {
        if (isDisabled) return;
        adjust(dir);
        holdTimeoutRef.current = setTimeout(() => {
            holdIntervalRef.current = setInterval(() => adjust(dir), 60);
        }, 350);
        window.addEventListener('mouseup', stopHold, { once: true });
        window.addEventListener('touchend', stopHold, { once: true });
    };

    useEffect(() => stopHold, []);

    const handleKeyDown = (event) => {
        if (blockNegativeChars && (event.key === '-' || event.key === 'e' || event.key === 'E')) {
            event.preventDefault();
            return;
        }
        if (step !== undefined && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
            event.preventDefault();
            adjust(event.key === 'ArrowUp' ? 'up' : 'down');
            return;
        }
        blurOnEnter(event);
        onKeyDown(event);
    };

    // Drop insignificant leading zeros as the user types, and clamp negatives that bypass the
    // keydown block (e.g. paste) up to `min`.
    const handleChange = (event) => {
        if (/^-?0[0-9]/.test(event.target.value)) {
            event.target.value = event.target.value.replace(/^(-?)0+(?=[0-9])/, '$1');
        }
        if (blockNegativeChars && event.target.value !== '') {
            const num = Number(event.target.value);
            if (!Number.isNaN(num) && num < 0) {
                onChange({ target: { name, value: String(minNum) } });
                return;
            }
        }
        onChange(event);
    };

    const numericValue = parseFloat(value);
    const upDisabled = isDisabled || (hasMax && !Number.isNaN(numericValue) && numericValue >= maxNum);
    const downDisabled = isDisabled || (hasMin && !Number.isNaN(numericValue) && numericValue <= minNum);

    return (
        <Field
            id={fieldId}
            label={label}
            labelEnd={labelEnd}
            helpText={helpText}
            status={status}
            isRequired={isRequired}
            isDisabled={isDisabled}
            tooltip={tooltip}
        >
            <div className="field-box">
                <input
                    ref={inputRef}
                    id={fieldId}
                    className="field-input field-number"
                    type="number"
                    name={name}
                    placeholder={placeholder}
                    disabled={isDisabled}
                    required={isRequired}
                    min={min}
                    max={max}
                    step={step !== undefined ? 'any' : undefined}
                    value={value}
                    autoFocus={autoFocus}
                    aria-label={ariaLabel}
                    aria-invalid={status === 'danger' || undefined}
                    aria-describedby={messageId}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={onBlur}
                />
                {/* Themed stepper replacing the browser's spin buttons, which are hidden in CSS.
                    tabIndex=-1 keeps it out of the tab order — keyboard users get the arrow keys. */}
                {step !== undefined && (
                    <span className="number-stepper" aria-hidden="true">
                        <button
                            type="button"
                            tabIndex={-1}
                            className="number-stepper-btn"
                            disabled={upDisabled}
                            onMouseDown={() => startHold('up')}
                            onMouseUp={stopHold}
                            onMouseLeave={stopHold}
                            onTouchStart={(event) => { event.preventDefault(); startHold('up'); }}
                            onTouchEnd={stopHold}
                        >
                            <FontAwesomeIcon icon={faChevronUp} />
                        </button>
                        <button
                            type="button"
                            tabIndex={-1}
                            className="number-stepper-btn"
                            disabled={downDisabled}
                            onMouseDown={() => startHold('down')}
                            onMouseUp={stopHold}
                            onMouseLeave={stopHold}
                            onTouchStart={(event) => { event.preventDefault(); startHold('down'); }}
                            onTouchEnd={stopHold}
                        >
                            <FontAwesomeIcon icon={faChevronDown} />
                        </button>
                    </span>
                )}
            </div>
        </Field>
    );
};

NumberInput.propTypes = {
    ...shared,
    min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    step: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    autoFocus: PropTypes.bool,
    onKeyDown: PropTypes.func,
};
